import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, X, MapPin, FileText, Building2 } from "lucide-react";
import { cityHref, stateHref } from "@/lib/urls";

export default function SmartSearch({ onClose, mobile = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q || q.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(() => {
      api
        .get(`/search`, { params: { q: q.trim() } })
        .then((r) => setResults(r.data))
        .finally(() => setLoading(false));
    }, 200);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  const flat = [
    ...(results?.cities || []).map((c) => ({ kind: "city", label: c.name, sub: c.state, to: cityHref(c.slug), count: c.installations })),
    ...(results?.states || []).map((s) => ({ kind: "state", label: s.name, sub: "State", to: stateHref(s.slug), count: s.installations })),
    ...(results?.blogs || []).map((b) => ({ kind: "blog", label: b.title, sub: b.category || "Blog", to: `/blog/${b.slug}` })),
  ];

  const go = (item) => {
    if (!item) return;
    navigate(item.to);
    onClose?.();
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[active] || flat[0]);
    } else if (e.key === "Escape") {
      onClose?.();
    }
  };

  return (
    <div
      className={`${mobile ? "fixed inset-0" : "fixed inset-0"} z-50 bg-black/40 backdrop-blur-sm`}
      data-testid="smart-search-overlay"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-2xl mx-auto mt-16 rounded-xl shadow-2xl overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <Search className="text-slate-400" size={20} />
          <input
            ref={inputRef}
            placeholder="Search cities, states, blogs…"
            className="flex-1 outline-none text-base placeholder:text-slate-400"
            value={q}
            onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onKey}
            data-testid="smart-search-input"
          />
          <kbd className="hidden md:inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-mono">ESC</kbd>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="smart-search-close">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto" data-testid="smart-search-results">
          {loading && (
            <div className="px-5 py-8 text-sm text-slate-500 text-center">Searching…</div>
          )}
          {!loading && !results && (
            <div className="px-5 py-8 text-sm text-slate-500 text-center">
              Type at least 2 characters to search across cities, states and blogs.
            </div>
          )}
          {!loading && results && flat.length === 0 && (
            <div className="px-5 py-8 text-sm text-slate-500 text-center">
              No results for "<strong>{q}</strong>". Try a different keyword.
            </div>
          )}
          {!loading && flat.length > 0 && (
            <div className="py-1">
              {flat.map((item, i) => (
                <button
                  key={item.to + i}
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(item)}
                  data-testid={`smart-search-result-${i}`}
                  className={`w-full text-left px-5 py-3 flex items-center gap-3 ${active === i ? "bg-slate-50" : ""}`}
                >
                  <div className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${
                    item.kind === "city" ? "bg-slate-50 text-slate-900" :
                    item.kind === "state" ? "bg-orange-50 text-[#EA580C]" :
                    "bg-emerald-50 text-emerald-600"
                  }`}>
                    {item.kind === "city" ? <MapPin size={16} /> :
                     item.kind === "state" ? <Building2 size={16} /> :
                     <FileText size={16} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold leading-tight truncate">{item.label}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {item.sub}{item.count ? ` · ${item.count}+ installations` : ""}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{item.kind}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-2 border-t border-slate-100 text-[11px] text-slate-500 flex items-center justify-between">
          <span>↑ ↓ navigate · Enter open</span>
          <span>Smart Infovision</span>
        </div>
      </div>
    </div>
  );
}
